import jsonFromDocument from "cerulean/src/JsonFromDocument";
import { useMemo } from "react";

// Mirrors the `current_user` context value produced by support.context_processors.support_user_context
type CurrentUser = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_narmi: boolean;
  is_superuser: boolean;
  session_oidc_provider: string | null;
  support_teams: string[] | null;
};

export type Roles = {
  narmi: boolean;
  superUser: boolean;
};

const isFeatureEnabled = (name: string, value?: unknown): boolean => {
  const features = Object.entries(jsonFromDocument("institution_features")).filter(
    ([, feature]) => feature
  );
  return features.some(([featureName, featureValue]: [string, unknown]) => {
    if (featureName === name) {
      if (!value) {
        return true;
      }
      if (typeof featureValue === "string") {
        return featureValue === value;
      }
      if (Array.isArray(featureValue)) {
        return featureValue.includes(value);
      }
    }
  });
};

export function useUserConfiguration(): {
  isFeatureEnabled: (name: string, value?: unknown) => boolean;
  features: string[];
  permissions: string[];
  currentUser: CurrentUser;
  roles: Roles;
} {
  // this might smell like a useless hook, but we might be moving to launchdarkly
  // and having a hook will future-proof us a little bit.
  const currentUser = useMemo(() => jsonFromDocument("current_user") as CurrentUser, []);
  const permissions = useMemo(() => jsonFromDocument("user_permissions"), []);
  const features = useMemo(() => {
    return Object.entries(jsonFromDocument("institution_features"))
      .filter(([, value]) => value)
      .map(([key]) => key);
  }, []);

  const roles = useMemo(() => {
    return {
      narmi: currentUser?.is_narmi || false,
      superUser: currentUser?.is_superuser || false,
    };
  }, [currentUser]);

  return {
    isFeatureEnabled,
    features,
    permissions,
    currentUser,
    roles,
  };
}

export type UserConfigurationType = ReturnType<typeof useUserConfiguration>;
